import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, casestudyCards } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const Fleetoz: React.FC = () => {
    return (
        <NewLayout
            title="FleetOz Automates Lead Capture on WhatsApp with Gallabox"
            description="Check out what FleetOz does to automate lead capture and close deals faster using WhatsApp chatbots for logistics."
            canonical="https://gallabox.com/case-study/fleetoz"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/fleetoz/fleetoz-og.png" }}
        >
            <CaseStudyHerosection
                title="FleetOz automates lead capture and closes deals faster using Gallabox’s WhatsApp bot"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/fleetoz/fleetoz-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "50%", label: " boost in productivity" },
                    { percentage: "48%", label: "increase in first-time conversions" },
                    { percentage: "20%", label: " savings in marketing spends" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Category", value: [" Logistics"] },
                                { label: "Location", value: [" India"] },
                            ]}
                            product={{
                                title: "Features Used",
                                productLink: [{ link: "https://gallabox.com/whatsapp-chatbot", label: "Whatsapp Chatbot" }],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <p>
                                <a href="https://www.fleetoz.com" className="text-blue-500">
                                    FleetOz
                                </a>
                                is an Indian B2B road travel management platform developed by Aurafox Solutions Pvt. Ltd. It offers a comprehensive solution for managing fleet
                                operations, including bookings, subcontracting, communication, and billing.
                            </p>
                            <p className="mt-5">The company is designed for organizations that:</p>
                            <FeatureList
                                List={[
                                    { listItem: "Own their fleet and need to manage internal vehicle bookings." },
                                    {
                                        listItem: "Wish to maximize fleet utilization by catering to internal use and subcontracting vehicles to customers.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 mb-3"
                            />
                            <p className="mt-5">FleetOz supports these organizations by providing tools for efficient booking management, communication, and billing processes.</p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The challenge"} className="gap-6" />

                            <p>
                                FleetOz, while being a tech-forward logistics company, faced a common yet costly challenge: their customers weren’t responding to traditional
                                outreach channels like emails or SMS—even when sent with important forms or links.
                            </p>
                            <p className="mt-5">
                                “Our operations team had to manually call and convince users to fill out forms. It was time-consuming, inefficient, and hard to scale,” shared Dev
                                Goyal, software developer at FleetOz.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"The search for a better tool"} />
                            <p>
                                While the FleetOz team had already worked with customer lifecycle management platforms like CleverTap, WebEngage, and MoEngage, they quickly
                                realized those tools were focused more on event-based journeys and not purpose-built for WhatsApp-driven automation.
                            </p>
                            <p className="mt-5">
                                They explored Twilio, Wati, and even Meta’s official docs, but these tools lacked either ease of setup or prebuilt flows optimized for WhatsApp
                                engagement.
                            </p>

                            <CaseStudyInformation subTitle={"Why Gallabox?"} />
                            <p>FleetOz selected Gallabox for three compelling reasons:</p>

                            <CaseStudyInformation subTitle={"Developer-friendly API docs"} />
                            <p>
                                Gallabox’s API documentation gave Dev’s tech team the confidence that they could build a custom WhatsApp bot that aligned with their lead capture
                                workflow. “We knew we could crack our bot use case because the API documentation was clear and actionable,” Dev noted.
                            </p>

                            <CaseStudyInformation subTitle={"Plug-and-play WhatsApp bot flow builder"} />
                            <p>
                                The initial setup experience was refreshingly straightforward. With Gallabox&apos;s drag-and-drop flow builder, FleetOz could launch their first
                                WhatsApp bot without waiting on developers or backend customizations. While Dev acknowledges there&apos;s room for improvement in UX, the current
                                framework got them from ideation to execution fast.
                            </p>

                            <CaseStudyInformation subTitle={"WhatsApp-first engagement"} />
                            <p>
                                Unlike legacy CRMs and marketing tools, Gallabox is built for WhatsApp-first communication. This matched FleetOz’s need to get real-time responses
                                from users via a channel they actually use—drastically increasing engagement rates on form submissions and queries.
                            </p>

                            <p className="mt-5">“We didn’t want another email engine. Gallabox gave us a real WhatsApp-native solution,” Dev emphasized.</p>
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"The results"} />
                            <p>By automating WhatsApp communication and reducing manual form outreach, FleetOz achieved measurable improvements:</p>

                            <FeatureList
                                List={[
                                    { listItem: "50% boost in team productivity" },
                                    { listItem: "48% increase in conversion rate for first-time users" },
                                    { listItem: "20% reduction in marketing/ad spend" },
                                    { listItem: "Overall, Dev rates Gallabox’s impact on business growth at 30%." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation subTitle={"Final thoughts"} />
                            <p>
                                For Dev, Gallabox proved to be highly functional for their specific WhatsApp bot use case. When it comes to recommending the tool to others, he
                                maintains that Gallabox is a serious contender if you&apos;re looking to build on WhatsApp.
                            </p>

                            <p className="mt-5">“Gallabox solved a critical gap for us — WhatsApp automation that just works.”</p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto text-center md:text-left">
                <section>
                    <h3 className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 text-center md:text-left">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCards.slice(0, 3)?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Fleetoz;
